import { useEffect, useRef, useState } from 'react';
import { submitLead } from '../utils/leads';
import { resolveTheme } from '../utils/theme';
import { Avatar } from './CardDisplay';

const field =
  'w-full h-12 px-3.5 rounded-xl border border-line-strong bg-white text-[16px] text-ink placeholder:text-ink-muted/70 focus:outline-none focus:ring-2 focus:border-transparent';

/**
 * "Share your details, get the contact" sheet shown on a public card.
 * Sharing is the main action; a small link still downloads the contact without it.
 */
export default function LeadForm({ card, theme, onDownload, onSkip, onClose }) {
  const t = resolveTheme(theme);
  const first = (card.full_name || '').split(' ')[0] || card.full_name;
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', company: '', note: '', website: '' });
  const [showNote, setShowNote] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const opened = useRef(Date.now());
  const firstInput = useRef(null);

  useEffect(() => {
    firstInput.current?.focus({ preventScroll: true });
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return setError('Please enter your name.');
    if (!form.email.trim() && !form.phone.trim()) return setError(`Add your email or phone so ${first} can reach you.`);
    setSending(true);
    try {
      await submitLead({ ...form, slug: card.slug, elapsed_ms: Date.now() - opened.current });
      setDone(true);
      onDownload();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const ring = { '--tw-ring-color': t.accent_color };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-navy/50 backdrop-blur-[2px]" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="lead-title" onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-[420px] max-h-[92vh] overflow-y-auto bg-white rounded-t-[28px] sm:rounded-[28px] shadow-2xl font-sans text-ink">
        <div className="relative px-6 pt-6 pb-2">
          <span className="sm:hidden absolute left-1/2 -translate-x-1/2 top-2.5 w-10 h-1 rounded-full bg-line" aria-hidden="true" />
          <button onClick={onClose} aria-label="Close" className="absolute right-3 top-3 w-10 h-10 rounded-full hover:bg-surface flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
          <div className="flex items-center gap-3 pr-10">
            <Avatar card={card} t={t} size={52} ringWidth={0} />
            <div className="min-w-0">
              <p className="text-sm text-ink-muted">Exchange contacts with</p>
              <p className="font-display font-bold text-lg leading-6 truncate">{card.full_name}</p>
            </div>
          </div>
        </div>

        {done ? (
          <div className="px-6 pb-7 pt-4 text-center">
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center" style={{ background: t.accentSoft }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={t.accent_color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
            </div>
            <h2 id="lead-title" className="font-display font-extrabold text-2xl mt-4">Thanks, {form.full_name.trim().split(' ')[0]}!</h2>
            <p className="text-ink-muted mt-2">{first} has your details, and {first}'s contact is downloading to your phone.</p>
            <button onClick={onDownload} className="mt-5 text-sm font-semibold hover:underline" style={{ color: t.accent_color }}>Download the contact again</button>
            <button onClick={onClose} className="mt-4 w-full h-12 rounded-xl border border-line-strong font-semibold hover:bg-surface">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="px-6 pb-6 pt-3 space-y-3">
            <h2 id="lead-title" className="font-display font-extrabold text-[26px] leading-8 tracking-tight">Share your details with {first}</h2>
            <p className="text-[15px] text-ink-muted -mt-1 mb-2">Send your contact and get {first}'s in return.</p>

            <label className="block">
              <span className="sr-only">Your name</span>
              <input ref={firstInput} className={field} style={ring} placeholder="Your name *" autoComplete="name" value={form.full_name} onChange={set('full_name')} maxLength={120} required />
            </label>
            <label className="block">
              <span className="sr-only">Email</span>
              <input className={field} style={ring} type="email" inputMode="email" placeholder="Email" autoComplete="email" value={form.email} onChange={set('email')} maxLength={254} />
            </label>
            <label className="block">
              <span className="sr-only">Phone</span>
              <input className={field} style={ring} type="tel" inputMode="tel" placeholder="Phone" autoComplete="tel" value={form.phone} onChange={set('phone')} maxLength={40} />
            </label>
            <label className="block">
              <span className="sr-only">Company</span>
              <input className={field} style={ring} placeholder="Company (optional)" autoComplete="organization" value={form.company} onChange={set('company')} maxLength={120} />
            </label>
            {showNote ? (
              <label className="block">
                <span className="sr-only">Note</span>
                <textarea className={`${field} h-24 py-3 resize-none`} style={ring} placeholder={`A note for ${first} (optional)`} value={form.note} onChange={set('note')} maxLength={1000} />
              </label>
            ) : (
              <button type="button" onClick={() => setShowNote(true)} className="text-sm font-semibold" style={{ color: t.accent_color }}>+ Add a note</button>
            )}
            {/* Hidden from people; bots fill it in */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')}
              className="absolute -left-[9999px] w-px h-px opacity-0" aria-hidden="true" />

            {error && <p role="alert" className="text-sm text-coral-ink">{error}</p>}

            <button type="submit" disabled={sending}
              className="w-full h-14 rounded-2xl font-semibold text-base transition hover:opacity-90 disabled:opacity-60 shadow-lg"
              style={{ background: t.accent_color, color: t.onAccent }}>
              {sending ? 'Sending...' : `Share & save ${first}'s contact`}
            </button>
            <p className="text-xs text-ink-muted text-center">Only {card.full_name} receives your details.</p>
            <div className="text-center pt-1">
              <button type="button" onClick={onSkip} className="text-[13px] text-ink-muted underline underline-offset-2 hover:text-ink">
                Download the contact without sharing
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
